import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2 } from "lucide-react";
import { useCart, formatAUD } from "@/context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [state, setState] = useState({ loading: true, paid: false, amount: null, currency: "aud" });
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState({ loading: false, paid: false });
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          if (!cleared.current) {
            cleared.current = true;
            clear();
          }
          setState({ loading: false, paid: true, amount: data.amount / 100, currency: data.currency });
          return;
        }
      } catch {
        // keep polling
      }
      attempts += 1;
      if (attempts < 15) setTimeout(poll, 2000);
      else setState({ loading: false, paid: false });
    };
    poll();
  }, [sessionId, clear]);

  return (
    <div data-testid="payment-success-page" className="min-h-screen bg-marble flex items-center justify-center px-6">
      <div className="marble-veil" />
      <div className="max-w-md w-full bg-white/80 backdrop-blur-xl border border-gold/30 rounded-sm p-12 text-center">
        {state.loading ? (
          <>
            <Loader2 size={40} className="text-gold animate-spin mx-auto mb-6" />
            <h1 className="font-serif-lux text-3xl text-charcoal">Confirming your order…</h1>
          </>
        ) : state.paid ? (
          <>
            <CheckCircle2 size={48} strokeWidth={1.25} className="text-gold mx-auto mb-6" />
            <h1 data-testid="order-confirmed-heading" className="font-serif-lux text-4xl text-charcoal mb-4">
              Order confirmed
            </h1>
            <p className="text-sm text-charcoal/60 font-light leading-relaxed mb-2">
              Thank you for choosing Altius. A confirmation will be sent to your email shortly.
            </p>
            {state.amount != null && (
              <p data-testid="order-amount" className="font-serif-lux text-2xl text-gold-dark mb-8">
                {formatAUD(state.amount)}
              </p>
            )}
            <Link
              data-testid="back-to-shop-link"
              to="/"
              className="inline-block px-10 py-4 bg-gold text-charcoal text-xs tracking-[0.25em] uppercase font-semibold rounded-full hover:bg-gold-dark transition-colors"
            >
              Continue Shopping
            </Link>
          </>
        ) : (
          <>
            <h1 className="font-serif-lux text-3xl text-charcoal mb-4">We're checking your payment</h1>
            <p className="text-sm text-charcoal/60 font-light mb-8">
              Your payment is taking a little longer to confirm. If you were charged, your order is safe with us.
            </p>
            <Link
              data-testid="back-to-shop-link"
              to="/"
              className="inline-block px-10 py-4 bg-gold text-charcoal text-xs tracking-[0.25em] uppercase font-semibold rounded-full hover:bg-gold-dark transition-colors"
            >
              Back to Shop
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
